import type { Device, Texture } from '@luma.gl/core';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import { useStore } from 'src/lib/player/store';
import type { FlagContent, PoiType, StarterType } from 'src/lib/player/store';
import MarkerContext from './MarkerContext';
import {
  type DrawInfo,
  DrawInfoType,
  createPoiDrawInfo,
  createStarterDrawInfo,
} from './createDrawInfos';

import ShelfPack from '@mapbox/shelf-pack';
import { make2DCanvas, realSubstring } from 'src/lib/player/utils';

const SPRITESHEET_SIZE = 2048;
const LARGE_MODE_SCALE = 2;

export type SpriteSheet = {
  index: number;
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  texture: Texture | null;
  shelf: ShelfPack;
};

export type Sprite = {
  hashKey: string;
  lat: number;
  lng: number;
  alpha: number;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  x: number;
  y: number;
  mask: boolean;
  sheetIndex: number;
  _starter?: StarterType;
  _poi?: PoiType;
};

class SpriteManager {
  sprites: { [key: string]: Sprite } = {};
  spritesheets: Array<SpriteSheet>;
  dirtySprites: Set<Sprite>;

  constructor() {
    this.spritesheets = [];
    this.dirtySprites = new Set();
    this.addSpritesheet(this.createSpritesheet(0));
  }

  createSpritesheet(index: number = this.spritesheets.length): SpriteSheet {
    const { canvas, context } = make2DCanvas(
      SPRITESHEET_SIZE,
      SPRITESHEET_SIZE,
      DEVICE_PIXEL_RATIO,
    );
    const shelf = new ShelfPack(
      Math.floor(SPRITESHEET_SIZE / DEVICE_PIXEL_RATIO),
      Math.floor(SPRITESHEET_SIZE / DEVICE_PIXEL_RATIO),
    );
    return {
      index,
      width: SPRITESHEET_SIZE,
      height: SPRITESHEET_SIZE,
      texture: null,
      canvas,
      context,
      shelf,
    };
  }

  addSpritesheet(sprite: SpriteSheet): void {
    this.spritesheets.push(sprite);
  }

  getCurrentSpritesheet(): SpriteSheet {
    return this.spritesheets[this.spritesheets.length - 1];
  }

  createMarker(drawInfo: DrawInfo, hashKey: string, scale = 1): Sprite {
    const measurement = MarkerContext.measure(drawInfo);
    const scaledMeasurement = {
      x: measurement.x * scale,
      y: measurement.y * scale,
      offsetX: measurement.offsetX * scale,
      offsetY: measurement.offsetY * scale,
    };
    let bin = this.getCurrentSpritesheet().shelf.packOne(
      scaledMeasurement.x + 1,
      scaledMeasurement.y + 1,
    );
    if (!bin) {
      this.addSpritesheet(this.createSpritesheet());
      bin = this.getCurrentSpritesheet().shelf.packOne(
        scaledMeasurement.x + 1,
        scaledMeasurement.y + 1,
      );
    }
    const sprite = {
      hashKey,
      lat: 0,
      lng: 0,
      alpha: 1,
      width: scaledMeasurement.x * DEVICE_PIXEL_RATIO,
      height: scaledMeasurement.y * DEVICE_PIXEL_RATIO,
      anchorX: scaledMeasurement.offsetX * DEVICE_PIXEL_RATIO,
      anchorY: scaledMeasurement.offsetY * DEVICE_PIXEL_RATIO,
      x: bin.x * DEVICE_PIXEL_RATIO,
      y: bin.y * DEVICE_PIXEL_RATIO,
      mask: false,
      sheetIndex: this.getCurrentSpritesheet().index,
    };

    if (drawInfo.type === DrawInfoType.POI_IMAGE) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      drawInfo.image = img;
      img.onload = () => {
        MarkerContext.draw(drawInfo, this.getCurrentSpritesheet().context, bin.x, bin.y, scale);
        this.dirtySprites.add(sprite);

        // workearound to trigger a rerender
        useStore.getState().actions.toggleTimer();
        useStore.getState().actions.toggleTimer();
      };
      img.src = drawInfo.icon || '';
    } else {
      MarkerContext.draw(drawInfo, this.getCurrentSpritesheet().context, bin.x, bin.y, scale);
    }

    this.dirtySprites.add(sprite);
    return sprite;
  }

  createPOISprite(poi: PoiType, largeMode: boolean): Sprite {
    const hashKey = `poi:${poi.id}-${poi.markerColor?.hexNum}:${largeMode}`;
    let sprite = this.sprites[hashKey];

    if (sprite == null) {
      const drawInfo = createPoiDrawInfo(
        poi.id,
        poi.shortName ?? realSubstring(poi.name, 0, 3),
        poi.name,
        poi.latlng,
        poi.markerColor?.hex ?? 'white',
        true,
        poi.icon,
      );
      sprite = this.createMarker(drawInfo, hashKey, largeMode ? LARGE_MODE_SCALE : 1);
      this.sprites = { ...this.sprites, [hashKey]: sprite };
    }
    sprite.lat = poi.latlng.lat;
    sprite.lng = poi.latlng.lng;
    sprite._poi = poi;
    return sprite;
  }

  createStarterSprite(
    starter: StarterType,
    lat: number,
    lng: number,
    opacity: number,
    flagContent: FlagContent,
    largeMode: boolean,
  ): Sprite {
    const hashKey = `starter:${starter.id}:${starter.markerColor.hexNum}:${starter.name}:${starter.startNumber}:${flagContent}:${largeMode}`;
    let sprite = this.sprites[hashKey];
    if (sprite == null) {
      const drawInfo = createStarterDrawInfo(starter, { lat, lng }, flagContent);
      sprite = this.createMarker(drawInfo, hashKey, largeMode ? LARGE_MODE_SCALE : 1);
      this.sprites = { ...this.sprites, [hashKey]: sprite };
    }
    sprite.lat = lat;
    sprite.lng = lng;
    sprite.alpha = opacity;
    sprite._starter = starter;
    return sprite;
  }

  updateDirtySpritesheets(device: Device): Array<SpriteSheet> {
    const dirtySpritesheets = new Set<SpriteSheet>();
    for (const sprite of this.dirtySprites) {
      dirtySpritesheets.add(this.spritesheets[sprite.sheetIndex]);
    }

    for (const spritesheet of this.spritesheets) {
      if (spritesheet.texture == null || dirtySpritesheets.has(spritesheet)) {
        const texture = device.createTexture({
          width: spritesheet.canvas.width,
          height: spritesheet.canvas.height,
        });
        texture.copyExternalImage({ image: spritesheet.canvas });
        spritesheet.texture = texture;
      }
    }
    this.dirtySprites.clear();
    return this.spritesheets;
  }
}

export default SpriteManager;
